export type AskResponse = {
  answer: string
  citations: Citation[]
  error?: string
  message_id?: string
  feedback?: Feedback
}

export type Citation = {
  part_index?: number
  content: string
  id: string
  title: string | null
  filepath: string | null
  url: string | null
  metadata: string | null
  chunk_id: string | null
  reindex_id: string | null
}

export type ToolMessageContent = {
  citations: Citation[]
  intent: string
}

export type ChatMessage = {
  id: string
  role: string
  content: string
  end_turn?: boolean
  date: string
  feedback?: Feedback
  context?: string
}

export type Conversation = {
  id: string
  title: string
  messages: ChatMessage[]
  date: string
}

export enum ChatCompletionType {
  ChatCompletion = 'chat.completion',
  ChatCompletionChunk = 'chat.completion.chunk'
}

export type ChatResponseChoice = {
  messages: ChatMessage[]
}

export type ChatResponse = {
  id: string
  model: string
  created: number
  object: ChatCompletionType
  choices: ChatResponseChoice[]
  history_metadata: {
    conversation_id: string
    title: string
    date: string
  }
  error?: any
}

export type ConversationRequest = {
  messages: ChatMessage[]
}

export type UserInfo = {
  access_token: string
  expires_on: string
  id_token: string
  provider_name: string
  user_claims: any[]
  user_id: string
}

export enum CosmosDBStatus {
  NotConfigured = 'Chat history is not configured',
  NotWorking = 'Chat history is not available',
  InvalidCredentials = 'Chat history has invalid credentials',
  InvalidDatabase = 'Invalid chat history database',
  InvalidContainer = 'Invalid chat history configuration',
  Working = 'Chat history is configured and working'
}

export type CosmosDBHealth = {
  cosmosDB: boolean
  status: string
}

export enum ChatHistoryLoadingState {
  Loading = 'loading',
  Success = 'success',
  Fail = 'fail',
  NotStarted = 'notStarted'
}

export type ErrorMessage = {
  title: string
  subtitle: string
}

export type UI = {
  title: string
  chat_title: string
  chat_description: string
  logo?: string
  chat_logo?: string
  show_share_button?: boolean
}

export type FrontendSettings = {
  auth_enabled?: string | null
  feedback_enabled?: string | null
  ui?: UI
  sanitize_answer?: boolean
}

export enum Feedback {
  Neutral = 'neutral',
  Positive = 'positive',
  Negative = 'negative',
  MissingCitation = 'missing_citation',
  WrongCitation = 'wrong_citation',
  OutOfScope = 'out_of_scope',
  InaccurateOrIrrelevant = 'inaccurate_or_irrelevant',
  OtherUnhelpful = 'other_unhelpful',
  HateSpeech = 'hate_speech',
  Violent = 'violent',
  Sexual = 'sexual',
  Manipulative = 'manipulative',
  OtherHarmful = 'other_harmlful'
}

// Authentication types
export type AgencyMembership = {
  agencyid: number
  agency_name: string
  role: string
}

export type AuthUser = {
  userid: number
  agencyid: number
  username: string
  email: string | null
  full_name: string | null
  role: string
  agency_name: string
  // Every agency this identity can switch between (active one is `agencyid`)
  agencies: AgencyMembership[]
}

export type LoginRequest = {
  username: string
  password: string
}

export type LoginResponse = {
  success: boolean
  user?: AuthUser
  error?: string
}

export type AuthState = {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  error: string | null
}

export type CVUploadTask = {
  task_id: string
  filename: string
  status: 'pending' | 'extracting_text' | 'ai_parsing' | 'checking_duplicates' | 'creating_candidate' | 'duplicate_found' | 'missing_fields' | 'completed' | 'failed'
  progress_message: string
  created_at: string
  updated_at: string
  error: string | null
  candidate_id: number | null
  candidate_name: string | null
  agencyid: number
  duplicate_info: {
    candidateid: number
    match_type: string
    first_name: string
    last_name: string
    email: string
    phone: string
  } | null
  extracted_data: Record<string, any> | null
  missing_fields: string[] | null
}
